import {
  PLUGIN_REGISTRY,
  TOOLBAR_GROUPS,
  ToolbarGroups,
  ToolbarButtons,
  PluginNames,
} from './registry';
import type { RawEditorOptions } from 'tinymce';
import { DEFAULT_STYLE } from './style';

export interface IEditorConfigFeatures {
  content?: {
    lists?: boolean;
    links?: boolean;
    images?: boolean;
    tables?: boolean;
    media?: boolean;
    searchReplace?: boolean;
    anchor?: boolean;
  };
  formatting?: {
    charmap?: boolean;
    textStyle?: boolean;
    colors?: boolean;
    alignment?: boolean;
    fonts?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    subscript?: boolean;
    superscript?: boolean;
    removeFormat?: boolean;
    indent?: boolean;
    bold?: boolean;
    italic?: boolean;
  };
  media?: {
    images?: boolean;
    video?: boolean;
    audio?: boolean;
  };
  utility?: {
    code?: boolean;
    fullscreen?: boolean;
    preview?: boolean;
    visualBlocks?: boolean;
    visualChars?: boolean;
    wordCount?: boolean;
    contextMenu?: boolean;
  };
  history?: {
    undo?: boolean;
    redo?: boolean;
  };
  template?: {
    variables?: boolean;
    links?: boolean;
    loops?: boolean;
    conditions?: boolean;
    functions?: boolean;
  };
  loadFromTemplate?: boolean;
}

export interface IEditorConfigOptions {
  toolbarGroups?: (keyof typeof TOOLBAR_GROUPS)[];
  features?: IEditorConfigFeatures;
  core?: {
    height?: number;
    placeholder?: string;
    menubar?: boolean;
    statusbar?: boolean;
    branding?: boolean;
  };
  additionalOptions?: Partial<RawEditorOptions>;
}

export const DEFAULT_CONFIG: Partial<RawEditorOptions> = {
  contextmenu_never_use_native: true,
  extended_valid_elements:
    'span[class|style|th:text|th:each|th:remove|contenteditable]',
  valid_elements: '*[*]',
  noneditable_class:
    'template-variable-highlight template-loop-highlight template-condition-highlight template-function-highlight template-row-condition-indicator',
  content_style: DEFAULT_STYLE,
  table_default_styles: {
    'border-collapse': 'collapse',
    border: '1px solid black',
    width: '100%',
  },
  images_upload_handler: (blobInfo: any) => {
    return new Promise<string>((resolve, reject) => {
      try {
        const base64 = blobInfo.base64();
        const mimeType = blobInfo.blob().type;
        resolve(`data:${mimeType};base64,${base64}`);
      } catch (error) {
        reject('Error while converting image to Base64.');
      }
    });
  },
  automatic_uploads: true,
};

export class EditorConfigBuilder {
  private plugins: Set<string> = new Set();
  private toolbarItems: string[] = [];
  private toolbarGroups: (keyof typeof TOOLBAR_GROUPS)[] = [];
  private config: Partial<RawEditorOptions> = {};

  constructor(options: IEditorConfigOptions = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      height: options.core?.height || 500,
      placeholder: options.core?.placeholder,
      menubar: options.core?.menubar || false,
      statusbar: options.core?.statusbar || false,
      branding: options.core?.branding || false,
      ...options.additionalOptions,
    };

    // Process features if provided
    if (options.features) {
      this.applyFeatures(options.features);
    }

    this.toolbarGroups = options.toolbarGroups || [];

    // Automatically enable plugins based on toolbar groups
    this.applyToolbarGroupPlugins();
  }

  /**
   * Automatically enable plugins required by the selected toolbar groups
   */
  private applyToolbarGroupPlugins(): void {
    this.toolbarGroups.forEach(group => {
      switch (group) {
        case ToolbarGroups.History:
          this.addToolbarButtons(ToolbarButtons.Undo, ToolbarButtons.Redo);
          break;
        case ToolbarGroups.Formatting:
          this.addToolbarButtons(
            ToolbarButtons.Bold,
            ToolbarButtons.Italic,
            ToolbarButtons.Underline,
            ToolbarButtons.Strikethrough
          );
          break;
        case ToolbarGroups.Alignment:
          this.addToolbarButtons(
            ToolbarButtons.AlignLeft,
            ToolbarButtons.AlignCenter,
            ToolbarButtons.AlignRight,
            ToolbarButtons.AlignJustify
          );
          break;
        case ToolbarGroups.Lists:
          this.enablePlugins(PluginNames.Lists, PluginNames.AdvList);
          this.addToolbarButtons(
            ToolbarButtons.BullList,
            ToolbarButtons.NumList
          );
          break;
        case ToolbarGroups.Media:
          this.enablePlugins(PluginNames.Image);
          this.addToolbarButtons(ToolbarButtons.Image);
          break;
        case ToolbarGroups.Link:
          this.enablePlugins(PluginNames.Link, PluginNames.Autolink);
          this.addToolbarButtons(ToolbarButtons.Link);
          break;
        case ToolbarGroups.Table:
          this.enablePlugins(PluginNames.Table);
          this.addToolbarButtons(ToolbarButtons.Table);
          break;
        case ToolbarGroups.Utility:
          this.enablePlugins(
            PluginNames.Code,
            PluginNames.Fullscreen,
            PluginNames.Preview
          );
          this.addToolbarButtons(
            ToolbarButtons.Code,
            ToolbarButtons.Fullscreen,
            ToolbarButtons.Preview
          );
          break;
        case ToolbarGroups.ClientUtility:
          this.enablePlugins(PluginNames.Fullscreen, PluginNames.Preview);
          this.addToolbarButtons(
            ToolbarButtons.Fullscreen,
            ToolbarButtons.Preview
          );
          break;
        case ToolbarGroups.TextStyle:
          this.addToolbarButtons(ToolbarButtons.Styles);
          break;
        case ToolbarGroups.Colors:
          this.addToolbarButtons(
            ToolbarButtons.ForeColor,
            ToolbarButtons.BackColor
          );
          break;
        case ToolbarGroups.Fonts:
          this.addToolbarButtons(
            ToolbarButtons.FontFamily,
            ToolbarButtons.FontSize
          );
          break;
        case ToolbarGroups.Indentation:
          this.enablePlugins(PluginNames.Lists);
          this.addToolbarButtons(ToolbarButtons.Indent, ToolbarButtons.Outdent);
          break;
        case ToolbarGroups.RemoveFormat:
          this.addToolbarButtons(ToolbarButtons.RemoveFormat);
          break;
        case ToolbarGroups.Download:
          this.addToolbarButtons(ToolbarButtons.ExportFile);
          break;
      }
    });
  }

  private applyFeatures(features: IEditorConfigFeatures): void {
    // Content features
    if (features.content) {
      if (features.content.lists)
        this.enablePlugins(PluginNames.Lists, PluginNames.AdvList);
      if (features.content.links)
        this.enablePlugins(PluginNames.Link, PluginNames.Autolink);
      if (features.content.images) this.enablePlugins(PluginNames.Image);
      if (features.content.tables) this.enablePlugins(PluginNames.Table);
      if (features.content.media) this.enablePlugins(PluginNames.Media);
      if (features.content.searchReplace)
        this.enablePlugins(PluginNames.SearchReplace);
      if (features.content.anchor) this.enablePlugins(PluginNames.Anchor);
    }

    // Formatting features
    if (features.formatting) {
      if (features.formatting.textStyle)
        this.addToolbarGroups(ToolbarGroups.TextStyle);
      if (features.formatting.colors)
        this.addToolbarGroups(ToolbarGroups.Colors);
      if (features.formatting.alignment)
        this.addToolbarGroups(ToolbarGroups.Alignment);
      if (features.formatting.fonts) this.addToolbarGroups(ToolbarGroups.Fonts);
      if (features.formatting.bold) this.addToolbarButtons(ToolbarButtons.Bold);
      if (features.formatting.italic)
        this.addToolbarButtons(ToolbarButtons.Italic);
      if (features.formatting.underline)
        this.addToolbarButtons(ToolbarButtons.Underline);
      if (features.formatting.strikethrough)
        this.addToolbarButtons(ToolbarButtons.Strikethrough);
      if (features.formatting.subscript)
        this.addToolbarButtons(ToolbarButtons.Subscript);
      if (features.formatting.superscript)
        this.addToolbarButtons(ToolbarButtons.Superscript);
      if (features.formatting.removeFormat)
        this.addToolbarGroups(ToolbarGroups.RemoveFormat);
      if (features.formatting.charmap) this.enablePlugins(PluginNames.CharMap);
    }

    // Media features
    if (features.media) {
      if (features.media.images) this.enablePlugins(PluginNames.Image);
      if (features.media.video || features.media.audio)
        this.enablePlugins(PluginNames.Media);
    }

    // Utility features
    if (features.utility) {
      if (features.utility.code) this.enablePlugins(PluginNames.Code);
      if (features.utility.fullscreen)
        this.enablePlugins(PluginNames.Fullscreen);
      if (features.utility.preview) this.enablePlugins(PluginNames.Preview);
      if (features.utility.visualBlocks)
        this.enablePlugins(PluginNames.VisualBlocks);
      if (features.utility.visualChars)
        this.enablePlugins(PluginNames.VisualChars);
      if (features.utility.wordCount) this.enablePlugins(PluginNames.WordCount);
      if (features.utility.contextMenu) {
        this.enablePlugins(PluginNames.ContextMenu);
        // If contextmenu is enabled but not configured in additionalOptions,
        // set default to use custom context menu (non-native)
        if (!this.config.contextmenu) {
          this.config.contextmenu_never_use_native = true;
        }
      }
    }

    if (features.history) {
      if (features.history.undo) this.addToolbarButtons(ToolbarButtons.Undo);
      if (features.history.redo) this.addToolbarButtons(ToolbarButtons.Redo);
    }

    if (features.template) {
      this.config = {
        ...this.config,
        contextmenu: `${this.config.contextmenu || ''}editVariable editLink editLoop editCondition editRowCondition editFunction`,
      };
      if (features.template.variables)
        this.addToolbarButtons(ToolbarButtons.TemplateVariable);
      if (features.template.links)
        this.addToolbarButtons(ToolbarButtons.TemplateLink);
      if (features.template.loops)
        this.addToolbarButtons(ToolbarButtons.TemplateLoop);
      if (features.template.conditions)
        this.addToolbarButtons(ToolbarButtons.TemplateCondition);
      if (features.template.functions)
        this.addToolbarButtons(ToolbarButtons.TemplateFunction);
    }
    if (features.loadFromTemplate) {
      this.addToolbarButtons(ToolbarButtons.LoadFromTemplate);
    }
  }

  enablePlugins(...pluginNames: string[]): this {
    pluginNames.forEach(name => {
      const plugin = PLUGIN_REGISTRY[name];
      if (!plugin) {
        console.warn(`Plugin "${name}" not found in registry`);
        // Still add it in case it's a valid TinyMCE plugin not in registry
        this.plugins.add(name);
        return;
      }

      // Add dependencies first
      if (plugin.dependencies) {
        plugin.dependencies.forEach(dep => {
          if (!this.plugins.has(dep)) {
            this.plugins.add(dep);
          }
        });
      }

      // Add the plugin
      this.plugins.add(name);

      // Add toolbar buttons if defined
      if (plugin.toolbarButtons) {
        plugin.toolbarButtons.forEach(button => {
          if (!this.toolbarItems.includes(button)) {
            this.toolbarItems.push(button);
          }
        });
      }
    });
    return this;
  }

  /**
   * Add toolbar groups to the toolbar configuration
   * @param groups - The toolbar groups to add.
   * @returns The builder instance.
   */
  addToolbarGroups(...groups: (keyof typeof TOOLBAR_GROUPS)[]): this {
    groups.forEach(group => {
      const buttons = TOOLBAR_GROUPS[group];
      if (buttons) {
        buttons.forEach(button => {
          if (!this.toolbarItems.includes(button)) {
            this.toolbarItems.push(button);
          }
        });
      }
    });
    return this;
  }

  /**
   * Add toolbar buttons to the toolbar configuration
   * @param buttons - The toolbar buttons to add.
   * @returns The builder instance.
   */
  addToolbarButtons(...buttons: ToolbarButtons[]): this {
    buttons.forEach(button => {
      if (!this.toolbarItems.includes(button)) {
        this.toolbarItems.push(button);
      }
    });
    return this;
  }

  /**
   * Build the full configuration of the editor based on the features and options provided
   * @returns The full configuration of the editor.
   */
  build(): Partial<RawEditorOptions> {
    return {
      ...this.config,
      plugins: Array.from(this.plugins).join(' '),
      toolbar: this.getToolbar(),
    };
  }

  /**
   * Build the toolbar based on toolbar groups and basic configuration of the editor
   * @returns The toolbar configuration string.
   */
  getToolbar(): string {
    const toolbar: string[] = [];
    this.toolbarGroups.forEach(group => {
      const buttons: string[] = [
        ...TOOLBAR_GROUPS[group as keyof typeof TOOLBAR_GROUPS],
      ];
      const buttonsEnabled = this.toolbarItems.filter(toolbarItem =>
        buttons.includes(toolbarItem)
      );
      if (buttonsEnabled) {
        toolbar.push(buttonsEnabled.join(' '));
      }
    });

    return toolbar.join(' | ');
  }
}
